/**
 * D1 — Voice channel block for the Campaign create wizard.
 *
 * Renders the voice-only fields appended to the create modal when
 * ``channel === "voice"``: trunk selector (from per-tenant
 * voice_trunks), agent selector (filtered to voice-enabled agents),
 * per-campaign daily spend cap, quiet-hour overrides, retry policy
 * (max retries + backoff), and the recording-consent disclosure
 * acknowledgement.
 *
 * Recipient validation is rendered separately by
 * ``RecipientListPreview`` and the pre-dispatch totals come from
 * ``CampaignLaunchConfirmationModal``.
 *
 * Per v3 invariant 4: tenant attestation MUST be completed before
 * voice campaigns can launch. The "Tenant not attested" warning is
 * a soft pre-flight nudge here; the backend re-checks at dispatch
 * time and returns 412.
 */
import React, { useEffect, useState } from "react";
import { COLORS } from "../../../theme/colors";
import { Badge } from "../../../components/ui";
import { outboundVoiceApi } from "../../../api/client";

// Pure: which agents are selectable for outbound voice. Exported for unit
// testing; the component consumes filterVoiceAgents(agents) below.
// Heuristic: any non-archived agent whose channels include "voice" OR whose
// flow_config.voice.voice_id is set. Backend re-validates on dispatch.
export function filterVoiceAgents(agents) {
  return (agents || []).filter(a => {
    if (a.status === "archived") return false;
    const ch = a.channels || a.flow_config?.channels || [];
    if (Array.isArray(ch) && ch.includes("voice")) return true;
    if (a.flow_config?.voice?.voice_id) return true;
    return false;
  });
}

// Pure: trunks that aren't soft-deleted. Exported for unit testing.
export function filterActiveTrunks(trunks) {
  return (trunks || []).filter(t => t.status !== "deleted" && !t.deleted_at);
}

export default function CampaignVoiceBlock({ value, onChange, agents = [] }) {
  // ``value`` shape (lives inside form.config under "voice_*" keys to
  // match the channel-prefix scrub in CampaignsView):
  //   voice_trunk_id, voice_agent_id, voice_daily_cap_usd,
  //   voice_max_retries, voice_quiet_hours_start, voice_quiet_hours_end,
  //   voice_recording_consent_disclosed
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });

  const [trunks, setTrunks] = useState([]);
  const [attestation, setAttestation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [trunksRes, attRes] = await Promise.all([
          outboundVoiceApi.listTrunks().catch(() => ({ data: [] })),
          outboundVoiceApi.attestationStatus().catch(() => null),
        ]);
        if (cancelled) return;
        setTrunks(trunksRes?.data || trunksRes || []);
        setAttestation(attRes || null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const voiceAgents = filterVoiceAgents(agents);

  const activeTrunks = filterActiveTrunks(trunks);
  const selectedTrunk = activeTrunks.find(t => t.id === v.voice_trunk_id);
  const trunkBreakerOpen = selectedTrunk?.circuit_breaker_state === "open";

  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "10px 12px",
    borderRadius: 8, border: `1px solid ${COLORS.border}`,
    background: COLORS.surface, color: COLORS.text, fontSize: 12,
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
      {/* G-LINK — deep link out of the wizard to the unified
          Voice Console so the operator can sanity-check tenant
          settings + drift + audit before launching. */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: -8 }}>
        <a
          href="#admin-voice-console"
          onClick={(e) => {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent("admin:goto-tab", { detail: { id: "voice_console" } }));
          }}
          style={{ fontSize: 11, color: COLORS.textMuted, textDecoration: "none" }}
        >
          Open Voice Console →
        </a>
      </div>

      {/* Attestation pre-flight */}
      {attestation && !attestation.attested && (
        <div style={{ padding: "10px 12px", borderRadius: 8, background: `${COLORS.pink}12`, border: `1px solid ${COLORS.pink}40`, fontSize: 12, color: COLORS.pink }}>
          ⚠ Tenant has not attested to outbound-voice posture. Voice campaigns will be rejected at dispatch (HTTP 412). Complete attestation in Admin → Outbound Voice first.
        </div>
      )}

      {loading && (
        <div style={{ fontSize: 12, color: COLORS.textMuted }}>Loading voice trunks & attestation status…</div>
      )}

      {/* Trunk selector */}
      <div>
        <label style={labelStyle}>Voice trunk</label>
        {activeTrunks.length === 0 && !loading ? (
          <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "10px 0" }}>
            No voice trunks configured. Create one in Admin → Voice Trunks first.
          </div>
        ) : (
          <select value={v.voice_trunk_id || ""} onChange={e => set("voice_trunk_id", e.target.value)} style={inputStyle}>
            <option value="">Select trunk…</option>
            {activeTrunks.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} · {t.caller_id_e164} · ${t.rate_per_minute_usd}/min · cap ${t.daily_spend_cap_usd}/day
                {t.circuit_breaker_state === "open" ? "  ⚠ BREAKER OPEN" : ""}
              </option>
            ))}
          </select>
        )}
        {trunkBreakerOpen && (
          <div style={{ fontSize: 11, color: COLORS.pink, marginTop: 4 }}>
            ⚠ Circuit breaker OPEN — dispatcher will refuse new calls on this trunk until it self-heals.
          </div>
        )}
      </div>

      {/* Agent selector */}
      <div>
        <label style={labelStyle}>Voice agent</label>
        {voiceAgents.length === 0 ? (
          <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "10px 0" }}>
            No voice-enabled agents found. Create an agent with voice channel + voice config in the Library first.
          </div>
        ) : (
          <select value={v.voice_agent_id || ""} onChange={e => set("voice_agent_id", e.target.value)} style={inputStyle}>
            <option value="">Select agent…</option>
            {voiceAgents.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )}
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
          Per runbook convention, voice agents are authored specifically for outbound campaigns (no per-agent voice toggle).
        </div>
      </div>

      {/* D1: first-message + AMD strategy */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={labelStyle}>Opening line (optional)</label>
          <input type="text" value={v.voice_first_message ?? ""}
            placeholder="e.g. Hi, calling about your insurance renewal."
            onChange={e => set("voice_first_message", e.target.value || null)}
            style={inputStyle} />
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
            Spoken right after the AI disclosure. Empty = agent improvises from system prompt.
          </div>
        </div>
        <div>
          <label style={labelStyle}>AMD (voicemail detection)</label>
          <select value={v.voice_amd_strategy ?? "agent"}
            onChange={e => set("voice_amd_strategy", e.target.value)} style={inputStyle}>
            <option value="off">Off — treat all as human</option>
            <option value="agent">Agent-side (Deepgram + LLM)</option>
            <option value="hybrid">Hybrid (carrier if available)</option>
            <option value="carrier">Carrier-only (managed Twilio)</option>
          </select>
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
            Default agent-side. Hybrid uses carrier AMD when trunk supports it.
          </div>
        </div>
      </div>

      {/* D1: voicemail behavior */}
      <div>
        <label style={labelStyle}>If we hit voicemail</label>
        <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 12 }}>
          <select value={v.voice_voicemail_action ?? "hangup"}
            onChange={e => set("voice_voicemail_action", e.target.value)} style={inputStyle}>
            <option value="hangup">Hang up (silent)</option>
            <option value="leave_message">Leave the script</option>
          </select>
          <input type="text" value={v.voice_voicemail_script ?? ""}
            placeholder="Optional script to leave on voicemail (max 2000 chars)"
            maxLength={2000}
            disabled={v.voice_voicemail_action !== "leave_message"}
            onChange={e => set("voice_voicemail_script", e.target.value || null)}
            style={{ ...inputStyle, opacity: v.voice_voicemail_action === "leave_message" ? 1 : 0.5 }} />
        </div>
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
          "Leave the script" requires a non-empty script — otherwise falls back to hangup.
        </div>
      </div>

      {/* D1: ring + max-call-duration timeouts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={labelStyle}>Ring timeout (seconds)</label>
          <input type="number" min="10" max="60" step="1"
            value={v.voice_ring_timeout_seconds ?? 30}
            onChange={e => set("voice_ring_timeout_seconds", Number(e.target.value))}
            style={inputStyle} />
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
            Default 30s. Range 10-60s.
          </div>
        </div>
        <div>
          <label style={labelStyle}>Max call duration (seconds)</label>
          <input type="number" min="30" max="1800" step="30"
            value={v.voice_max_call_duration_seconds ?? 600}
            onChange={e => set("voice_max_call_duration_seconds", Number(e.target.value))}
            style={inputStyle} />
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
            Default 600s (10min). Hard ceiling 1800s (30min, per design Invariant #11).
          </div>
        </div>
      </div>

      {/* D1: campaign-level concurrency + spend cap */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={labelStyle}>Max concurrent calls</label>
          <input type="number" min="1" max="100" step="1"
            value={v.voice_max_concurrent_calls ?? ""}
            placeholder={selectedTrunk ? `≤ ${selectedTrunk.max_concurrent_calls} (trunk cap)` : "Trunk cap"}
            onChange={e => set("voice_max_concurrent_calls", e.target.value === "" ? null : Number(e.target.value))}
            style={inputStyle} />
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
            Per-campaign concurrency cap. Cannot exceed trunk cap. Empty = inherit trunk.
          </div>
        </div>
        <div>
          <label style={labelStyle}>Daily spend cap (USD)</label>
          <input type="number" step="0.01" min="0" value={v.voice_daily_cap_usd ?? ""}
            placeholder={selectedTrunk ? String(selectedTrunk.daily_spend_cap_usd) : "10.00"}
            onChange={e => set("voice_daily_cap_usd", e.target.value === "" ? null : Number(e.target.value))}
            style={inputStyle} />
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
            Per-campaign cap. Empty = use trunk cap.
          </div>
        </div>
      </div>

      {/* D1: retry policy (proper voice_retry JSON object, not voice_max_retries) */}
      <div style={{ padding: "10px 12px", borderRadius: 8, background: `${COLORS.surface}`, border: `1px solid ${COLORS.border}` }}>
        <label style={{ ...labelStyle, marginBottom: 8 }}>Retry policy</label>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr 1fr", gap: 12, alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: COLORS.text }}>
            <input type="checkbox"
              checked={(v.voice_retry?.enabled !== false)}
              onChange={e => set("voice_retry", {
                ...(v.voice_retry || {}),
                enabled: e.target.checked,
                max_attempts: v.voice_retry?.max_attempts ?? 2,
                backoff_minutes: v.voice_retry?.backoff_minutes ?? [60],
                retry_on: v.voice_retry?.retry_on ?? ["no_answer", "busy"],
              })} />
            Enabled
          </label>
          <div>
            <label style={{ fontSize: 10, color: COLORS.textDim }}>Max attempts</label>
            <input type="number" min="1" max="5" value={v.voice_retry?.max_attempts ?? 2}
              onChange={e => set("voice_retry", { ...(v.voice_retry || { enabled: true, retry_on: ["no_answer", "busy"], backoff_minutes: [60] }), max_attempts: Number(e.target.value) })}
              style={{ ...inputStyle, padding: "6px 10px" }} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: COLORS.textDim }}>Backoff between attempts (minutes)</label>
            <input type="number" min="5" max="1440" value={(v.voice_retry?.backoff_minutes?.[0]) ?? 60}
              onChange={e => set("voice_retry", { ...(v.voice_retry || { enabled: true, max_attempts: 2, retry_on: ["no_answer", "busy"] }), backoff_minutes: [Number(e.target.value)] })}
              style={{ ...inputStyle, padding: "6px 10px" }} />
          </div>
        </div>
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 6 }}>
          Defaults: ON, 2 attempts, retry on no_answer + busy. Honors quiet hours.
        </div>
      </div>

      {/* Quiet hours override */}
      <div>
        <label style={labelStyle}>Quiet hours (recipient local time)</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <input type="time" value={v.voice_quiet_hours_start || "21:00"}
            onChange={e => set("voice_quiet_hours_start", e.target.value)} style={inputStyle} />
          <input type="time" value={v.voice_quiet_hours_end || "09:00"}
            onChange={e => set("voice_quiet_hours_end", e.target.value)} style={inputStyle} />
        </div>
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
          Default 21:00–09:00 per TCPA. Calls outside this window are deferred to the next allowed slot, NOT skipped.
        </div>
      </div>

      {/* Recording consent acknowledgement */}
      <div style={{ padding: "10px 12px", borderRadius: 8, background: `${COLORS.accent}08`, border: `1px solid ${COLORS.border}` }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={!!v.voice_recording_consent_disclosed}
            onChange={e => set("voice_recording_consent_disclosed", e.target.checked)}
            style={{ marginTop: 2 }} />
          <div style={{ fontSize: 12, color: COLORS.text }}>
            <strong>Recording disclosure acknowledged.</strong>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
              Agent prompt includes the platform's standard "This call may be recorded for quality assurance"
              opener. Required for all two-party-consent jurisdictions. Customer-specific wording overrides
              live in the agent's system prompt.
            </div>
          </div>
        </label>
      </div>

      {/* Summary chip */}
      {selectedTrunk && v.voice_agent_id && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Badge color={COLORS.accent}>Ready to validate recipients</Badge>
          <span style={{ fontSize: 11, color: COLORS.textMuted }}>
            Click "Validate & launch" below to see per-recipient eligibility + cost estimate before dispatch.
          </span>
        </div>
      )}
    </div>
  );
}
